import React, { useRef, useEffect } from 'react';
import { HelpCircle, Info } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext.tsx';

interface AssistantIdentityFormProps {
  persistOnChange?: boolean;
  assistantName: string;
  setAssistantName: (val: string) => void;
  assistantRole: string;
  setAssistantRole: (val: string) => void;
  assistantResponsibility: string;
  setAssistantResponsibility: (val: string) => void;
  assistantFormality: string;
  setAssistantFormality: (val: string) => void;
  assistantTone: string;
  setAssistantTone: (val: string) => void;
  assistantLanguage: string;
  setAssistantLanguage: (val: string) => void;
  disabledResponsibility?: boolean;
}

const InfoTooltip: React.FC<{ text: string }> = ({ text }) => (
  <span className="relative inline-flex group" tabIndex={0} aria-label={text}>
    <Info className="h-3.5 w-3.5 text-gray-400 transition-colors group-hover:text-sminy-primary" />
    <span
      role="tooltip"
      className="pointer-events-none absolute left-1/2 top-5 z-20 w-64 max-w-[calc(100vw-2rem)] -translate-x-1/2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-left text-xs font-normal leading-5 text-gray-700 opacity-0 shadow-xl transition-opacity group-hover:opacity-100 group-focus:opacity-100"
    >
      {text}
    </span>
  </span>
);

// Helper components
const Field: React.FC<{ label: string; children: React.ReactNode; hint?: string; tooltip: string; required?: boolean }> = ({
  label,
  children,
  hint,
  tooltip,
  required = true
}) => {
  const { isDark } = useTheme();
  return (
    <label className="block text-sm">
      <span className={`mb-1 flex items-center gap-1.5 ${isDark ? 'text-gray-300' : 'text-gray-700'
        }`}>
        <span>{label}{required && <span className="text-red-500"> *</span>}</span>
        <InfoTooltip text={tooltip} />
      </span>
      {children}
      {hint && <span className={`mt-1 block text-[11px] ${isDark ? 'text-gray-400' : 'text-gray-500'
        }`}>{hint}</span>}
    </label>
  );
};

const Input = React.forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement>>((props, ref) => {
  const { isDark } = useTheme();
  return (
    <input
      ref={ref}
      {...props}
      className={`w-full rounded-xl border px-3 py-2 text-sm outline-none transition-all focus:ring-2 focus:ring-sminy-primary ${isDark
        ? 'border-gray-600 bg-gray-700 text-gray-200 placeholder:text-gray-400 focus:border-sminy-primary'
        : 'border-gray-300 bg-white text-gray-800 placeholder:text-gray-400 focus:border-sminy-primary'
        } ${props.className ?? ""}`}
    />
  );
});

const AssistantIdentityForm: React.FC<AssistantIdentityFormProps> = ({
  persistOnChange = false,
  assistantName, setAssistantName,
  assistantRole, setAssistantRole,
  assistantResponsibility, setAssistantResponsibility,
  assistantFormality, setAssistantFormality,
  assistantTone, setAssistantTone,
  assistantLanguage, setAssistantLanguage,
  disabledResponsibility = false
}) => {
  const { isDark } = useTheme();

  // Refs para inputs não controlados (mantém performance original)
  const nameRef = useRef<HTMLInputElement>(null);
  const roleRef = useRef<HTMLInputElement>(null);
  const responsibilityRef = useRef<HTMLInputElement>(null);

  // Inicializar os valores dos inputs
  useEffect(() => {
    if (nameRef.current) nameRef.current.value = assistantName;
    if (roleRef.current) roleRef.current.value = assistantRole;
    if (responsibilityRef.current) responsibilityRef.current.value = assistantResponsibility;
  }, [assistantName, assistantRole, assistantResponsibility]);

  // Atualizar estado apenas quando o input perde o foco
  const handleNameBlur = () => {
    if (nameRef.current) setAssistantName(nameRef.current.value);
  };

  const handleRoleBlur = () => {
    if (roleRef.current) setAssistantRole(roleRef.current.value);
  };

  const handleResponsibilityBlur = () => {
    if (responsibilityRef.current) setAssistantResponsibility(responsibilityRef.current.value);
  };

  return (
    <div className="space-y-4">
      {persistOnChange && <h2 className="register-section-title">Identidade</h2>}
      {/* Nome do Agente - usando refs (funcionalidade original) */}
      <Field label="Nome do Agente" hint="Ex: Ana" tooltip="Nome que o paciente verá ou ouvirá quando falar com o assistente.">
        <Input
          ref={nameRef}
          type="text"
          required
          defaultValue={assistantName}
          onChange={persistOnChange ? (e) => setAssistantName(e.target.value) : undefined}
          onBlur={handleNameBlur}
          placeholder="Ex: Ana"
        />
      </Field>

      {/* Função/Cargo - usando refs (funcionalidade original) */}
      <Field label="Função/Cargo" tooltip="Papel usado para apresentar o assistente no atendimento.">
        <Input
          ref={roleRef}
          type="text"
          required
          defaultValue={assistantRole}
          onChange={persistOnChange ? (e) => setAssistantRole(e.target.value) : undefined}
          onBlur={handleRoleBlur}
          placeholder="Ex: Consultora de agendamento"
        />
      </Field>

      {/* Responsabilidade principal - usando refs (funcionalidade original) */}
      <Field label="Responsabilidade principal" tooltip="Objetivo principal do assistente no fluxo de atendimento. Este campo fica travado para preservar o padrão de agendamento.">
        <Input
          ref={responsibilityRef}
          type="text"
          required
          defaultValue={assistantResponsibility}
          onChange={persistOnChange ? (e) => setAssistantResponsibility(e.target.value) : undefined}
          onBlur={handleResponsibilityBlur}
          disabled={disabledResponsibility}
          placeholder="Ex: Auxiliar no agendamento de consultas"
          className={disabledResponsibility ? '!bg-gray-200 !text-gray-500 cursor-not-allowed border-gray-300' : ''}
        />
      </Field>

      {persistOnChange && <h2 className="register-section-title">Personalidade</h2>}
      {/* Selects permanecem controlados (funcionalidade original) */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Nível de formalidade" tooltip="Define se o assistente deve responder de forma mais formal, informal ou equilibrada.">
          <select
            required
            className={`w-full rounded-xl border px-3 py-2 text-sm focus:ring-2 focus:ring-sminy-primary ${isDark
              ? 'border-gray-600 bg-gray-700 text-gray-200 focus:border-sminy-primary'
              : 'border-gray-300 bg-white text-gray-800 focus:border-sminy-primary'
              }`}
            value={assistantFormality}
            onChange={e => setAssistantFormality(e.target.value)}
          >
            <option value="formal">Formal</option>
            <option value="informal">Informal</option>
            <option value="intermediário">Intermediário</option>
          </select>
        </Field>

        <Field label="Tom de voz" tooltip="Ajusta a sensação da conversa para combinar com a postura da clínica.">
          <select
            required
            className={`w-full rounded-xl border px-3 py-2 text-sm focus:ring-2 focus:ring-sminy-primary ${isDark
              ? 'border-gray-600 bg-gray-700 text-gray-200 focus:border-sminy-primary'
              : 'border-gray-300 bg-white text-gray-800 focus:border-sminy-primary'
              }`}
            value={assistantTone}
            onChange={e => setAssistantTone(e.target.value)}
          >
            <option value="amigável">Amigável</option>
            <option value="profissional">Profissional</option>
            <option value="casual">Casual</option>
            <option value="empático">Empático</option>
            <option value="entusiástico">Entusiástico</option>
          </select>
        </Field>
      </div>

      <Field label="Idioma principal" tooltip="Idioma padrão usado pelo assistente nas conversas com pacientes.">
        <select
          required
          className={`w-full rounded-xl border px-3 py-2 text-sm focus:ring-2 focus:ring-sminy-primary ${isDark
            ? 'border-gray-600 bg-gray-700 text-gray-200 focus:border-sminy-primary'
            : 'border-gray-300 bg-white text-gray-800 focus:border-sminy-primary'
            }`}
          value={assistantLanguage}
          onChange={e => setAssistantLanguage(e.target.value)}
        >
          <option value="pt-BR">Português (Brasil)</option>
          <option value="pt-PT">Português (Portugal)</option>
          <option value="en">Inglês</option>
          <option value="es">Espanhol</option>
        </select>
      </Field>

      {/* Dica com cores da empresa */}
      <div className={`rounded-2xl border p-4 ${isDark
        ? 'border-sminy-primary/30 bg-sminy-primary/10'
        : 'border-sminy-primary/20 bg-sminy-primary/5'
        }`}>
        <div className="flex gap-3">
          <HelpCircle className="w-5 h-5 text-sminy-primary flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="font-medium text-sminy-primary mb-1">Dica de personalidade</h4>
            <p className={`text-sm ${isDark ? 'text-sminy-primary/90' : 'text-sminy-primary/80'
              }`}>
              Defina uma personalidade que represente bem sua clínica. Uma personalidade amigável e empática geralmente funciona bem para o setor odontológico.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssistantIdentityForm;
